import { GetStaticProps } from 'next'
import { NextSeo } from 'next-seo'

import Footer from '@/components/footer'
import { graphcms, IReview, REVIEW_QUERY } from '@/lib/hygraph'
import ReviewsSection from '@/modules/company/components/reviews-section'
import {
  CARDS,
  LOCATION_BASED_FAQS,
  LOCATION_BASED_KEY_FEATURES,
  LOCATION_BASED_LOGOS,
  LOCATION_BASED_SOLUTION_EMPOWERS,
  LOCATION_BASED_STATS,
} from '@/modules/solutions/constants/location-based'
import EmpowerSection from '@/modules/solutions/containers/empower-section'
import FaqSection from '@/modules/solutions/containers/faq-section'
import HeroSection from '@/modules/solutions/containers/hero-section'
import KeyFeaturesSection from '@/modules/solutions/containers/key-features-section'
import ProvideSection from '@/modules/solutions/containers/provide-section'
import StatsSection from '@/modules/solutions/containers/stats-section'
import SummarySection from '@/modules/solutions/containers/summary-section'

export default function LocationBasedAnalytics({
  reviews,
}: {
  reviews: IReview[]
}) {
  return (
    <>
      <NextSeo
        title="Mindsite Solutions | Location-Based Analytics for Strategic Stock Management"
        description="Monitor product availability across warehouses and retailer locations. Measure location efficiency and make better decisions based on location-based analytics."
      />

      <main className="overflow-hidden">
        <HeroSection
          infiniteScrollItems={LOCATION_BASED_LOGOS}
          infiniteScrollClassNames="mx-auto max-w-[1200px]"
          title="Location-Based Analytics for Strategic Stock Management"
          infoSummary="Monitor product availability across warehouses and retailer locations. Measure location efficiency and make better decisions based on location-based analytics."
          image={{
            src: '/images/solutions/location-based/hero.png',
            alt: 'Location-Based Analytics for Strategic Stock Management',
            width: 523,
            height: 273,
          }}
        />
        <StatsSection
          infoTitle="Mindsite optimizes stock management and prevents revenue loss with real-time location-based tracking."
          stats={LOCATION_BASED_STATS}
        />
        <ProvideSection
          cards={CARDS}
          title={'What We Provide\n to Optimize Product Availability'}
          infoSummary="Location-Based Analytics provides critical insights by monitoring geographical data in real time. You can better manage product availability and improve location efficiency."
        />
        <KeyFeaturesSection
          title="Key Features of Mindsite's Location-Based Analytics"
          features={LOCATION_BASED_KEY_FEATURES}
          video={{
            src: '/media/solutions/multi-location.mp4',
            poster: '/media/solutions/multi-location-poster.png',
            id: 'desktop-video-player',
          }}
        />
        <EmpowerSection
          title="Location-Based Analytics That Empowers Your E-Commerce Success"
          features={LOCATION_BASED_SOLUTION_EMPOWERS}
        />
        <ReviewsSection
          reviews={reviews}
          title="What Makes Us the Trusted Partner of Top Brands"
          titleClassname="text-xl font-semibold text-center [text-wrap:pretty] text-zinc-900 sm:text-2xl md:text-2xl lg:text-4xl/relaxed"
          sliderItemClassname="bg-solutions max-lg:[&>div]:flex-col max-lg:[&>div]:gap-10"
        />

        <FaqSection faqs={LOCATION_BASED_FAQS} />
        <SummarySection>
          <h2 className="text-xl font-semibold text-[#212427] sm:text-2xl lg:text-5xl/relaxed">
            Optimize Inventory <br /> with Location-Based Insights
          </h2>
          <p className="text-xs text-zinc-600 sm:text-base lg:text-2xl">
            Leverage real-time geographical data to optimize your inventory
            strategy.
            <br />
            <span className="font-medium">
              Track product availability, understand regional market demands,
              and align your supply chain efficiently.
            </span>
          </p>
        </SummarySection>
      </main>
      <Footer />
    </>
  )
}

export const getStaticProps: GetStaticProps<{
  reviews: IReview[]
}> = async () => {
  try {
    const reviews = (await graphcms
      .request(REVIEW_QUERY)
      .then((res: any) => res.reviews)) as IReview[]

    return { props: { reviews } }
  } catch (error) {
    console.error(error)
  }

  return {
    props: {
      reviews: [],
    },
    notFound: true,
  }
}
